import {
  getFormFieldIdWithProductSuffix,
  useTexts,
} from "Components/TextContext";
import {
  Placeholder,
  ValueType,
} from "Components/TextContext/commonComponentEnums";
import {
  AdditionalBuildingHeader,
  AdditionalBuildingsWrap,
  ParagraphAsHint,
  ParagraphAsLabel,
} from "../houseProductsStyled";
import {
  AdditionalBuilding,
  AdditionalBuildingFormTypes,
  AdditionalBuildingFormValues,
  AdditionalBuildingType,
} from "./additionalBuildingsInterfaces";
import {
  mapBuildingIsolation,
  mapFromFMSBuildingType,
  mapToFMSBuildingType,
} from "./additionalBuildingsUtils";

import { Option as SelectOption } from "@storeblocks/select";
import { StyledRow } from "App/global-style";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { buttonTrackId } from "Utils/analytics";
import { nullableFormBooleanToNullableBoolean } from "Utils/formUtils";
import { createKey } from "Utils/generalUtils";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagTask } from "Utils/unleash/flags";
import { FormikProps } from "formik";
import React from "react";
import { useDispatch } from "react-redux";
import { FormGroupWrap } from "../../../Advice/ProductDetails/FormElements/styled";
import { AddBuilding } from "./Components/AddBuilding";
import { AddBuildingButton } from "./Components/AddBuildingButton";

interface Props {
  formProps: FormikProps<AdditionalBuildingFormTypes>;
  onClick: () => void;
  setAdditionalBuildings: (status: boolean) => void;
  additionalBuildings: boolean;
  error?: boolean;
  updateFormValues: (values: any) => void;
  product: ProductBackendName;
  stepId: string;
}

export const AdditionalBuildings = ({
  formProps,
  setAdditionalBuildings,
  additionalBuildings,
  error,
  updateFormValues,
  product,
  stepId,
}: Props): React.ReactElement | null => {
  const dispatch = useDispatch();
  const { getText, getSelectOptions } = useTexts();
  const isAdditionalBuildingFromEiendsomverdiEnabled = useUnleashFeatureFlag(
    FeatureFlagTask.SSX_1816_ADDITIONALBUILDING_FROM_EIENDOMSVERDI,
  );

  function addAdditionalBuilding(
    formValues: AdditionalBuildingFormValues,
  ): void {
    const tempArray: AdditionalBuilding[] = [];
    tempArray.push({
      id: createKey(),
      isolated:
        formValues.additionalBuilding_buildingType &&
        mapBuildingIsolation(formValues.additionalBuilding_buildingType),
      type:
        formValues.additionalBuilding_buildingType &&
        mapFromFMSBuildingType(formValues.additionalBuilding_buildingType),
      areal: formValues.additionalBuilding_areal,
      constructionYear: formValues.additionalBuilding_constructionYear,
      ...(mapFromFMSBuildingType(formValues.additionalBuilding_buildingType) ===
        AdditionalBuildingType.ANNEX && {
        boa: formValues.additionalBuilding_boa,
        hasTapWater:
          nullableFormBooleanToNullableBoolean(
            formValues.additionalBuilding_hasTapWater,
          ) ?? false,
        hasElectricity:
          nullableFormBooleanToNullableBoolean(
            formValues.additionalBuilding_hasElectricity,
          ) ?? false,
        standard: formValues.additionalBuilding_standard,
      }),
    } as AdditionalBuilding);
    const updatedArray = formProps.values.otherBuildings.concat(tempArray);
    dispatch(
      updatePriceCalculationFormState({
        stepId: stepId,
        formState: {
          ...formProps.values,
          otherBuildings: updatedArray,
        },
      }),
    );
    setAdditionalBuildings(false);
  }

  function removeAdditionalBuilding(
    formValues: AdditionalBuildingFormTypes,
    buildingId: string,
  ): void {
    const tempArray = formValues.otherBuildings.filter(
      (otherBuilding: AdditionalBuilding) => {
        return buildingId !== otherBuilding.id;
      },
    );
    dispatch(
      updatePriceCalculationFormState({
        stepId: stepId,
        formState: {
          ...formValues,
          otherBuildings: tempArray,
        },
      }),
    );
  }

  return (
    <FormGroupWrap>
      <ParagraphAsLabel>
        {getText("additionalbuildings.label.header-house")}
      </ParagraphAsLabel>
      <ParagraphAsHint>
        {getText("additionalbuildings.hint.header-house")}
      </ParagraphAsHint>

      {formProps.values.otherBuildings.map((building: AdditionalBuilding) => {
        const formField = getFormFieldIdWithProductSuffix(
          product,
          "additionalBuilding_buildingType",
        );

        const buildingTypeOptions = getSelectOptions(
          TextComponentNameLevel1.ADDITIONAL_BUILDINGS,
          formField,
          getText(Placeholder.CHOOSE_TYPE),
        ).filter(
          (item) =>
            isAdditionalBuildingFromEiendsomverdiEnabled ||
            item.value !== AdditionalBuildingType.GARAGE_OUTHOUSE_ANNEX,
        );

        return (
          <AdditionalBuildingsWrap key={building.id}>
            {buildingTypeOptions.map((option: SelectOption) => {
              if (option.value === mapToFMSBuildingType(building)) {
                return (
                  <>
                    <StyledRow>
                      <AdditionalBuildingHeader>
                        {option.label}
                      </AdditionalBuildingHeader>
                      <ThemedButton
                        variant="icon"
                        iconRight="close"
                        aria-label="close_button"
                        trackId={buttonTrackId("remove-additional-building")}
                        onClick={() =>
                          building.id &&
                          removeAdditionalBuilding(
                            formProps.values,
                            building.id,
                          )
                        }
                      />
                    </StyledRow>
                    <p>
                      {`${getText(ValueType.AREA)}: ${building.areal} ${getText(
                        ValueType.SQUARE_METERS,
                      )}, ${getText(ValueType.CONSTRUCTION_YEAR)}: ${
                        building.constructionYear
                      }`}
                    </p>
                  </>
                );
              }
              return null;
            })}
          </AdditionalBuildingsWrap>
        );
      })}
      {additionalBuildings && (
        <AddBuilding
          addAdditionalBuilding={addAdditionalBuilding}
          setAdditionalBuildings={setAdditionalBuildings}
          product={product}
          error={error}
        />
      )}

      <AddBuildingButton
        onClick={() => {
          updateFormValues(formProps);
          setAdditionalBuildings(true);
        }}
        buttonText={getText("additionalbuildings.buttons.addbuilding")}
      />
    </FormGroupWrap>
  );
};
